/**
 * The destructive-action confirm for the admin infrastructure screens.
 *
 * AUTHORED — no Figma exists for the admin surface (brief, Acceptance:
 * "class AUTHORED"). Assembled from parts the app already ships: the Radix
 * Dialog shell `LostReasonDialog` uses (overlay ink/40, `bg-surface`,
 * `radius/md`, the overlay shadow token), the existing `Button` variants, and
 * the `status/danger` pair for the one word that must not read as routine.
 * No new colour, no new radius, no new type size.
 *
 * The confirm NAMES ITS TARGET. "Revoke 'Warehouse PC'?" is answerable;
 * "Are you sure?" is not — the operator with three keys open in three tabs
 * needs the dialog to say which one is about to stop working.
 *
 * Unlike the show-once key dialog, this one CAN be dismissed by overlay click
 * or Escape: cancelling a destructive action must always be the easy path.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../components/ui';

export interface ConfirmDialogProps {
  open: boolean;
  /** The question, naming the target: "Revoke 'Warehouse PC — Kinshasa'?" */
  title: string;
  /**
   * What happens if they proceed — consequences, not ceremony.
   *
   * `ReactNode`, not `string`, since the §1.7 warnings confirm: that dialog states a
   * LIST of server-authored consequences and joining them into one paragraph would bury
   * the second and third. Every other caller passes a string and is unaffected.
   */
  body: React.ReactNode;
  /** The verb on the button, saying what it does: "Revoke key". */
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.JSX.Element {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(420px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-surface p-6 shadow-[var(--crf-shadow-overlay)]">
          <Dialog.Title className="text-h3 text-fg">{title}</Dialog.Title>
          <Dialog.Description className="mt-2 text-label text-fg-secondary">
            {body}
          </Dialog.Description>
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            {/* The `Button` primary is indigo; a destructive confirm painted the
              * action colour invites the reflex click. `bg-danger-fg` is the
              * AA-nudged `status/danger/text` #ca2525 used AS A FILL — white on
              * it measures 5.52:1 (mode-invariant), comfortably AA, and notably
              * better than the 4.22:1 the indigo primary would have given.
              *
              * CALLOUT — THE HOVER AND PRESS WERE ALPHA FADES, AND THE LIGHT
              * PRESS FAILED AA. This carried `hover:bg-danger-fg/90
              * active:bg-danger-fg/80` until prove-time finding L1-2. An
              * opacity modifier composites against the SURFACE, so the one
              * class string behaved differently per theme (white on the
              * composite, floored 2dp): light 5.52 → 4.83 → **4.09 ✗**, dark
              * 5.52 → 6.28 → 7.13 ✓. Contrast FELL below the 4.5 floor in
              * light at the moment of the press while RISING in dark — the
              * D1a defect signature, invisible to anyone testing in dark.
              *
              * Both states now take `bg-brand-hover` — brand/primary-hover
              * #b91f1f · VariableID:1:13, the design's own darker step of this
              * same red family. White = 6.40, mode-invariant, contrast rises.
              * No third authored red step exists, so active HOLDS the hover
              * value rather than inventing one. Identical treatment to the
              * vendored `ConfirmModal`, which is where these callers migrate
              * (WEB-TIER-A-LEDGER item 1); this is the contrast repair only,
              * so the seven admin callers stop failing AA before that lands.
              * Pinned in `styles/tokens.contrast.test.ts`. */}
            <Button
              onClick={onConfirm}
              disabled={busy}
              className="bg-danger-fg text-fg-inverse hover:bg-brand-hover active:bg-brand-hover"
            >
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
